import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import startCase from 'lodash.startcase';
import isEmpty from 'lodash.isempty';
import SectionLoader from '../Loader/SectionLoader';
import { ModalTitle } from '../BasicModal/BasicModal';
import BasicModalBody from '../BasicModal/BasicModalBody';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  productLocationTypes,
  productLocationTypesByName,
} from '../../util/enum/api/productTypes';
import Paragraph from '../Paragraph/Paragraph';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import useProductPrice from '../../util/hooks/useProductPrice';
import { ReactComponent as Money } from '../../assets/images/svg/money.svg';
import { ReactComponent as Vault } from '../../assets/images/svg/vault.svg';
import { ReactComponent as Delivery } from '../../assets/images/svg/package.svg';
import { ReactComponent as Card } from '../../assets/images/svg/credit-card.svg';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import BuyInputFields from '../InputFields/BuyInputFields';
import ModalButtons from '../Modal/ModalButtons';
import { uFlexColumn } from '../../assets/styles/utility';
import RowItemElement from './RowItem';
import useProductCCPrice from '../../util/hooks/useProductCCPrice';
import SelectVaultLocation from './SelectVaultLocation';
import SelectStorageType from './SelectStorageType';
import SelectAddress from './SelectAddress';
import {
  ADD_ADDRESS_WIZARD,
  IS_FUND_MODAL_OPEN,
  SIGNIFYD_SESSION_ID,
  STANDARD_DEPOSIT_WIZARD,
  STATUS_NEW_ADDRESS_ADDED,
  // WIZARD_DATA,
} from '../../constants/sessionStorage';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import AddDeliveryAddressModal from '../Modals/Address/DeliveryAddressModal';
import {
  getCurrentAccount,
  getCurrentAccountUid,
  selectAccountAddresses,
} from '../../store/selectors/accountSelectors';
import { fetchAccountAddresses } from '../../store/actions/account/accountActions';
import addressTagType, {
  ADDRESS_TAG_TYPES,
  ADDRESS_TAG_TYPES_ICONS,
} from '../../util/enum/api/addressTagType';
import {
  // selectIsLoadingByActionType,
  selectIsLoadingByActionTypes,
} from '../../store/selectors/loadingSelectors';
import {
  ACCOUNT_SETTINGS_LOADING,
  FETCH_ACCOUNT_ADDRESSES_LOADING,
} from '../../store/actions/account/accountActionConstants';
import {
  getPriceForLocation,
  getQuotesForBuyDelivery,
  getQuotesForOrderStorageBuy,
} from '../../store/actions/orders/orderActions';
import { selectLocationPrices } from '../../store/selectors/buyStorageSelectors';
import { selectBalancesAvailable } from '../../store/selectors/portfolioBalancesSelectors';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';
import SelectPaymentOptions from './SelectPaymentOptions';
import { PAYMENT_METHOD_TYPES_ICONS } from '../../util/enum/api/paymentTypes';
import useGetCountries from '../../util/hooks/useGetCountries';
import { ORDER_ENTRY_TYPES } from '../../util/enum/api/orderEntryTypes';
import {
  selectProductTiers,
  // selectProductTiersError,
} from '../../store/selectors/productsSelector';
// import { PRODUCT_TIERS_LOADING } from '../../store/actions/products/productActionConstants';
import { fetchProductTiers } from '../../store/actions/products/productActions';
import { ALL_ZEROES_UID } from '../../constants/mockScenarios';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import StandardDepositWizard from '../Modals/Deposit/StandardDepositWizard/StandardDepositWizard';
import IraDepositWizard from '../Modals/DepositWizard/IraDepositWizard';
import { IRA_ENTRUST_TYPE, IRA_EQUITY_TYPE } from '../../util/constants';
import MilleniumDepositWizard from '../Modals/DepositWizard/MilleniumDepositWizard';
import { deleteWizardKey } from '../../util/helpers/wizardHelpers';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import { generateSessionOrderId } from '../../util/helpers/sinigfyd';
import loadSignifydDeviceFingerprintScript, {
  loadSignifydDeviceFingerprintScriptWithoutSessionId,
} from '../../signifydDeviceFingerprint';
import { useBuyFlowStepNameEvent } from '../../util/helpers/crmEventsHelper';
import {
  BUY_FLOW_ORDER_TYPE,
  BUY_FLOW_STEPS_ENUM,
} from '../../util/enum/api/crmEvents';

const TabsContainer = styled.div`
  display: flex;
  width: 100%;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${variables.borderRadius.borderRadius};
  overflow: hidden;
  height: 44px;
  margin-bottom: ${pxToRem(16)};
`;

const TabButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  font-size: ${pxToRem(16)};
  font-weight: 500;
  border: none;
  cursor: pointer;
  background: ${(props) =>
    props.active
      ? themeColors.colorBackgroundPrimary
      : themeColors.colorBackgroundSecondary};
  color: ${themeColors.colorTextPrimary};
  box-shadow: ${(props) =>
    props.active ? '0 3px 8px rgba(0,0,0,0.24)' : 'none'};
  border-radius: ${(props) =>
    props.active ? variables.borderRadius.borderRadius : '0'};
  transition: all 0.2s ease;
  margin: 4px;
`;

// Placeholder for icons (replace with SVG later)
const Icon = styled.span`
  display: inline-block;
  margin-right: 8px;
`;

const ProductPrice = styled(Paragraph)`
  font-weight: 600;
  font-size: ${pxToRem(20)};
  line-height: 1.4;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: ${pxToRemMd(14)};
  }
`;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  h3 {
    flex: 1;
  }
`;

const PriceWrapper = styled.div`
  ${uFlexColumn};
  text-align: right;

  svg {
    vertical-align: middle;
    margin-right: 4px;
  }
`;

const PriceToggle = styled(Paragraph)`
  font-size: ${pxToRem(14)};
  font-weight: 600;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: ${pxToRemMd(12)};
  }
`;

const FormContent = styled.div`
  display: flex;
  flex: 1;
`;

const ButtonWrapper = styled.div`
  button {
    height: ${pxToRem(44)};
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    button {
      height: ${pxToRemMd(44)};
    }
  }
`;

const VaultTag = styled.div`
  font-size: ${pxToRem(12)};
  color: ${(props) =>
    props.segregated ? themeColors.colorWhite : themeColors.colorWhite};
  font-weight: 600;
  background-color: ${(props) =>
    props.segregated
      ? themeColors.colorButtonPrimary
      : themeColors.colorSuccess};
  border-radius: ${variables.borderRadius.borderRadiusSm};
  display: inline-flex;
  padding: ${pxToRem(4)} ${pxToRem(6)};
  justify-content: center;
  align-items: center;
  margin-left: ${pxToRem(10)};
`;

const OrderSelection = ({
  wizardData,
  isProductPage,
  setWizardData,
  isPostPaidVisible,
  handleNext,
  setWizardTitle,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setGoBackHandler,
  stepIndex,
  currentStepIndex,
  isGS,
  openSupport,
  isSupportOpen,
  isUtmaAccount,
  isIRAAccount,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { storageProduct } = wizardData;
  const locations = wizardData?.storageProduct.Locations || [];
  const selectedVault = wizardData?.selectedVault || {};
  const addresses = useSelector(selectAccountAddresses);
  const accountUid = useSelector(getCurrentAccountUid);
  const locationPrices = useSelector(selectLocationPrices);
  const balance = useSelector(selectBalancesAvailable);
  const countries = useGetCountries();
  const productTiers = useSelector(selectProductTiers);
  const brokerCode = useBrokerCode();
  const account = useSelector(getCurrentAccount);
  // const productTiersError = useSelector(selectProductTiersError);
  // const isLoadingTiers = useSelector(
  //   selectIsLoadingByActionType(PRODUCT_TIERS_LOADING),
  // );

  const [openSelectVaultLocation, setOpenSelectVaultLocation] = useState(false);
  const [openSelectStorageType, setOpenSelectStorageType] = useState(false);
  const [openSelectAddress, setOpenSelectAddress] = useState(false);
  const [openSelectPaymentOptions, setOpenSelectPaymentOptions] = useState(
    false,
  );
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const isNewAddressAdded = retrieveFromSessionStorage(
    STATUS_NEW_ADDRESS_ADDED,
  );
  const [selectedAddress, setSelectedAddress] = useState(
    () => wizardData?.deliveryAddress || {},
  );
  const paymentMethods = balance?.PaymentMethods;
  const availableForTrading = balance?.AvailableForTrading;
  const [updatedPaymentMethods, setUpdatedPaymentMethods] = useState(
    paymentMethods,
  );

  const [isLoadingGetQuotes, setIsLoadingGetQuotes] = useState(false);
  const isLoading = useSelector(
    selectIsLoadingByActionTypes([
      FETCH_ACCOUNT_ADDRESSES_LOADING,
      ACCOUNT_SETTINGS_LOADING,
    ]),
  );
  const [isVaultPricesLoading, setIsVaultPricesLoading] = useState(false);
  const isSegregated = wizardData?.selectedPlan === 'premium';
  const [orderValues, setOrderValues] = useState({});
  const [savingAmount, setSavingAmount] = useState(0);
  const [activeTab, setActiveTab] = useState(wizardData?.activeTab || 'vault');
  const [activeType, setActiveType] = useState(
    wizardData?.activeType || 'quantity',
  );
  const selectedPlan = wizardData?.selectedPlan || 'standard';

  const isVaultActiveTab = activeTab === 'vault';
  const balancePaymentMethodUid = ALL_ZEROES_UID;

  const {
    displayIRAEntrustOptionsPermission,
    displayIRAMilleniumOptionsPermission,
    displayIRAEquityOptionsPermission,
    displayStandardOptionsPermission,
  } = useCheckPermissions();
  const handleEventBuyFlowStep = useBuyFlowStepNameEvent();

  const [isFundsModalOpen, setIsFundsModalOpen] = useState(false);

  const minPrice = locations?.length
    ? Math.min(...locations.map((l) => l.PricePerUnit))
    : 0;
  const firstLowestVault = locations.find(
    (l) => l.PricePerUnit === minPrice && l.LocationType !== 12,
  );

  const verifiedAddresses = addresses.filter((el) => el.IsVerified);

  useEffect(() => {
    if (stepIndex === currentStepIndex) {
      setGoBackHandler(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, currentStepIndex]);

  useEffect(() => {
    setWizardTitle(t('buyWizard.buy'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!wizardData?.selectedVault && firstLowestVault) {
      setWizardData({
        ...wizardData,
        selectedVault: firstLowestVault,
      });
    }
  }, [wizardData, firstLowestVault]); //eslint-disable-line

  const unitCCPrice = useProductCCPrice(
    wizardData?.storageProduct,
    wizardData?.selectedVault,
    isProductPage,
  );
  const unitPrice = useProductPrice(
    wizardData?.storageProduct,
    wizardData?.selectedVault,
    isProductPage,
  );

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [dispatch, accountUid]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVaultSymbol = (wizardData) => {
    const selectedVault = wizardData?.selectedVault;

    const match = locations.find(
      (loc) =>
        loc.LocationType === selectedVault?.LocationType &&
        loc.ProductCode === selectedVault?.ProductCode,
    );

    return match?.SymbolCode || null;
  };

  const symbol = getVaultSymbol(wizardData);

  useEffect(() => {
    if (symbol) {
      dispatch(
        fetchProductTiers({
          accountUid,
          symbol,
          side: 'Buy',
          location: !isVaultActiveTab
            ? Object.keys(productLocationTypesByName).find(
                (key) => productLocationTypesByName[key] === 12,
              )
            : parseEnumType(
                productLocationTypes,
                wizardData?.selectedVault?.LocationType,
              ),
          trueTiers: true,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, dispatch, symbol, activeTab]);

  useEffect(() => {
    const updatedPaymentMethodsList = paymentMethods?.map((paymentMethod) => {
      if (!paymentMethod.PaymentMethodUid) {
        return { ...paymentMethod, PaymentMethodUid: '1' };
      }
      return paymentMethod;
    });
    setUpdatedPaymentMethods(updatedPaymentMethodsList);
  }, [paymentMethods]);

  useEffect(() => {
    const isDefaultPaymentMethod = paymentMethods?.find(
      (paymentMethod) => paymentMethod?.IsDefault,
    );
    const accountBalance = paymentMethods?.find(
      (paymentMethod) => paymentMethod?.PaymentMethodType === 1,
    );
    if (!isDefaultPaymentMethod) {
      setSelectedPaymentMethod();
    }
    if (isDefaultPaymentMethod && !selectedPaymentMethod) {
      setSelectedPaymentMethod(isDefaultPaymentMethod);
    }
    if (!isPostPaidVisible) {
      setSelectedPaymentMethod(accountBalance);
    }
    setWizardData((prev) => ({
      ...prev,
      selectedPaymentMethod,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethods, accountUid]);

  const paymentMethodRowItem = () => {
    if (selectedPaymentMethod) {
      if (selectedPaymentMethod?.PaymentMethodType === 1) {
        return t('buyWizard.payment.paymentMethods.AccountBalance');
      }
      if (selectedPaymentMethod?.PaymentMethodType === 2) {
        return `${selectedPaymentMethod?.BankName}, ${'***'}${
          selectedPaymentMethod?.BankAccountMask
        }`;
      }
      if (selectedPaymentMethod?.PaymentMethodType === 3) {
        return `${t('buyWizard.payment.paymentMethods.CreditCard')} ${'***'}${
          selectedPaymentMethod?.Last4Digits
        }`;
      }
    } else {
      return t('buyWizard.payment.emptyPaymentRow');
    }
  };

  useEffect(() => {
    const addressesList = brokerCode === 'HAA' ? verifiedAddresses : addresses;

    if (addressesList || !isEmpty(addressesList)) {
      const defaultAddress = isNewAddressAdded
        ? addressesList[addressesList.length - 1]
        : addressesList?.find((address) => address.IsDefaultMailingAddress);
      if (defaultAddress?.IsPoBox) {
        setSelectedAddress({});
      } else {
        setSelectedAddress(defaultAddress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses, isNewAddressAdded]);

  const isDeliveryAddressSelected = (address) => {
    if (address.AddressUid === selectedAddress?.AddressUid) {
      return true;
    }

    return false;
  };

  const [pricePerUnit, setPricePerUnit] = useState(unitPrice);
  const [priceCCPerUnit, setPriceCCPerUnit] = useState(unitCCPrice);

  useEffect(() => {
    if (unitPrice !== undefined && unitPrice !== null) {
      setPricePerUnit(unitPrice);
    }
  }, [unitPrice, dispatch]); //eslint-disable-line

  useEffect(() => {
    if (unitCCPrice !== undefined && unitCCPrice !== null) {
      setPriceCCPerUnit(unitCCPrice);
    }
  }, [unitCCPrice, dispatch]); //eslint-disable-line

  const vaultTag = () => {
    const { selectedPlan, selectedVault } = wizardData || {};

    if (selectedPlan === 'premium') {
      return <VaultTag segregated>{t('buyWizard.segregated')}</VaultTag>;
    }

    if (
      (!selectedPlan || selectedPlan === 'standard') &&
      selectedVault?.PricePerUnit === minPrice
    ) {
      return <VaultTag>{t('buyWizard.bestPrice')}</VaultTag>;
    }

    return null;
  };

  useEffect(() => {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const existingScript = document.getElementById('sig-api');
    if (!sessionId) {
      if (existingScript) {
        existingScript.dataset.orderSessionId = generateSessionOrderId();
        storeInSessionStorage(
          SIGNIFYD_SESSION_ID,
          existingScript.dataset.orderSessionId,
        );
        return;
      }
      loadSignifydDeviceFingerprintScript();
      const script = document.getElementById('sig-api');
      storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);
    } else if (!existingScript) {
      loadSignifydDeviceFingerprintScriptWithoutSessionId(sessionId);
    }
  }, []);

  const DeliveryAddress = () => {
    if (!addresses || (brokerCode === 'HAA' && isEmpty(verifiedAddresses))) {
      return <Paragraph>{t('buyWizard.noAddress')}</Paragraph>;
    }
    if (addresses && selectedAddress && selectedAddress?.TagType) {
      return (
        <>
          {ADDRESS_TAG_TYPES_ICONS[selectedAddress?.TagType]}{' '}
          {selectedAddress?.TagType === ADDRESS_TAG_TYPES.OTHER
            ? selectedAddress?.TagNote
            : addressTagType[selectedAddress?.TagType]}
        </>
      );
    }
    if (addresses && selectedAddress && !selectedAddress?.TagType) {
      return selectedAddress?.AddressLine;
    }
  };

  const isSingleLocation = locations.length === 1;

  const sortedLocations = [...locationPrices].sort(
    (a, b) => a.LocationPrice - b.LocationPrice,
  );

  const fetchVaultLocationPrices = () => {
    if (!accountUid || !storageProduct) return;

    const quantityOrAmount =
      activeType === 'quantity'
        ? orderValues.QuantityAmount || 1
        : orderValues.CashAmount || 0;

    const unitType = activeType === 'quantity' ? 'Quantity' : 'Cash';
    const paymentMethodType = wizardData?.paymentMethodType || 'AccountBalance';

    setOpenSelectVaultLocation(true);
    setIsVaultPricesLoading(true);

    dispatch(
      getPriceForLocation({
        accountUid,
        data: {
          productCode: storageProduct?.ProductCode,
          quantityOrAmount,
          unitType,
          paymentMethodType,
        },
        onSuccess: () => {
          setIsVaultPricesLoading(false);
        },
        onError: () => {
          setIsVaultPricesLoading(false);
        },
        isLoading: setIsVaultPricesLoading,
      }),
    );
  };

  const onHandleGetQuotesSuccess = (getQuoteResult) => {
    const {
      QuoteUid,
      QuoteStatusItem: {
        QuantityExecuted,
        ClientPricePerUnit,
        TotalClientExecutionPrice,
        VolumeDiscount,
        PaymentMethodStepData,
        PaymentTypeDiscount,
        DeliveryFee,
        SalesTax,
      },
      ExpiresOnUtc,
    } = getQuoteResult;
    const calculation = {
      estimatedOrderTotal: TotalClientExecutionPrice,
      newPricePerUnit: ClientPricePerUnit,
      volumeDiscount: VolumeDiscount,
      quantity: QuantityExecuted,
      paymentMethodStepData: PaymentMethodStepData,
      paymentTypeDiscount: PaymentTypeDiscount,
      deliveryFee: DeliveryFee,
      salesTax: SalesTax,
    };
    handleEventBuyFlowStep(
      account,
      BUY_FLOW_STEPS_ENUM.SELECT_AMOUNT,
      wizardData?.storageProduct,
      BUY_FLOW_ORDER_TYPE.buyForDelivery,
    );
    setWizardData({
      ...wizardData,
      selectedPaymentMethod,
      activeType,
      calculation,
      quoteID: QuoteUid,
      expiryTime: ExpiresOnUtc,
      isVaultActiveTab,
      activeTab,
      selectedAddress,
      savingAmount,
      updatedPaymentMethods,
      productTiers,
    });

    handleNext();
  };

  useEffect(() => {
    if (!isEmpty(orderValues)) {
      setWizardData({
        ...wizardData,
        orderValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderValues]);

  const submitForm = (values) => {
    const paymentTypeUid =
      selectedPaymentMethod?.PaymentMethodType !== 1
        ? selectedPaymentMethod?.PaymentMethodUid
        : balancePaymentMethodUid;
    setOrderValues({
      CashAmount: activeType === 'dollars' ? values.CashAmount : 0,
      QuantityAmount: activeType === 'quantity' ? values.QuantityAmount : 0,
    });

    const orderEntry = isPostPaidVisible
      ? {
          orderEntryType: ORDER_ENTRY_TYPES.ORDER_ENTRY,
        }
      : {};
    const payload = {
      getQuotesPayload: {
        accountUid,
        symbolCode: selectedVault?.SymbolCode,
        quantityOrAmount:
          activeType === 'dollars'
            ? values?.CashAmount
            : values?.QuantityAmount,
        sideType: 'Buy',
        unitType: activeType === 'dollars' ? 'Cash' : 'Quantity',
        paymentType: selectedPaymentMethod?.PaymentMethodType,
        addressUid: selectedAddress?.AddressUid,
      },
      isLoading: setIsLoadingGetQuotes,
      onSuccess: onHandleGetQuotesSuccess,
      // onError: onHandleGetQuotesError,
      isSegregated,
      paymentTypeUid,
      addPaymentMethodData: true,
      ...orderEntry,
    };
    setIsLoadingGetQuotes(true);

    if (isVaultActiveTab) {
      dispatch(
        getQuotesForOrderStorageBuy({
          ...payload,
        }),
      );
    } else {
      dispatch(
        getQuotesForBuyDelivery({
          ...payload,
        }),
      );
    }
  };

  if (isAddAddressOpen) {
    return (
      <AddDeliveryAddressModal
        isOpen={isAddAddressOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_ADDRESS_WIZARD);
          setIsAddAddressOpen(false);
        }}
        title={t('buyWizard.addYourDeliveryAddress')}
        page="buy"
        avoidClearingStorage
        isAddAddressOpen={isAddAddressOpen}
        setIsAddAddressOpen={setIsAddAddressOpen}
      />
    );
  }

  return (
    <>
      <SectionLoader isLoading={isLoading || isLoadingGetQuotes}>
        <BasicModalBody>
          {!isSingleLocation &&
            !isIRAAccount &&
            !isUtmaAccount &&
            !isProductPage && (
              <TabsContainer>
                <TabButton
                  active={activeTab === 'vault'}
                  onClick={() => setActiveTab('vault')}
                >
                  <Icon>
                    <Vault />
                  </Icon>
                  {t('buyWizard.vault')}
                </TabButton>
                <TabButton
                  active={activeTab === 'delivery'}
                  onClick={() => setActiveTab('delivery')}
                >
                  <Icon>
                    <Delivery />
                  </Icon>
                  {t('buyWizard.delivery')}
                </TabButton>
              </TabsContainer>
            )}
          <HeadingWrapper>
            <ModalTitle
              marginBottom={4}
            >{`${storageProduct?.ShortCaption}`}</ModalTitle>
            <PriceWrapper>
              <ProductPrice>
                {selectedPaymentMethod?.PaymentMethodType !== 3
                  ? formatMoneyNumeral(pricePerUnit)
                  : formatMoneyNumeral(priceCCPerUnit)}
              </ProductPrice>

              {/* <ProductPrice>
              {formatMoneyNumeral(storageProduct.ProductPrice)}
            </ProductPrice> */}
              {isPostPaidVisible && !isUtmaAccount && (
                <PriceToggle>
                  {selectedPaymentMethod?.PaymentMethodType === 3 ? (
                    <>
                      <Card width={20} height={20} />
                      {t('buyWizard.card')}
                    </>
                  ) : (
                    <>
                      <Money width={20} height={20} />
                      {t('buyWizard.cashAchTag')}
                    </>
                  )}
                </PriceToggle>
              )}
            </PriceWrapper>
          </HeadingWrapper>

          <Formik
            initialValues={{
              CashAmount: wizardData?.orderValues?.CashAmount || '',
              QuantityAmount: wizardData?.orderValues?.QuantityAmount || '',
            }}
            enableReinitialize
            // validationSchema={buyForStorageValidationSchema(
            //   activeType,
            //   pricePerUnit,
            //   // balanceAvailableForTrading,
            //   productLocationTypes,
            //   selectedVault,
            //   // getMinimumOrderAmount,
            // )}
            onSubmit={submitForm}
            validateOnBlur={false}
          >
            {({
              values,
              setFieldValue,
              // errors,
              setFieldTouched,
            }) => (
              <Form>
                <FormContent>
                  <BuyInputFields
                    activeType={activeType}
                    setActiveType={setActiveType}
                    values={values}
                    setFieldValue={setFieldValue}
                    selectedVault={wizardData?.selectedVault}
                    setFieldTouched={setFieldTouched}
                    unitPrice={pricePerUnit}
                    priceCCPerUnit={priceCCPerUnit}
                    setOrderValues={setOrderValues}
                    // displayError={displayError}
                    // setGetQuotesError={setGetQuotesError}
                    locationForTiers={parseEnumType(
                      productLocationTypes,
                      wizardData?.selectedVault?.LocationType,
                    )}
                    symbol={symbol}
                    setPricePerUnit={setPricePerUnit}
                    setPriceCCPerUnit={setPriceCCPerUnit}
                    // onValuesChange={setFormValues}
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSavingAmount={setSavingAmount}
                    productTiers={productTiers}
                    isGS={isGS}
                    isPostPaidVisible={isPostPaidVisible}
                    availableForTrading={availableForTrading}
                  />
                </FormContent>
                {!isSingleLocation && !isIRAAccount && (
                  <RowItemElement
                    label={
                      activeTab === 'delivery'
                        ? t('buyWizard.delivery')
                        : t('buyWizard.vault')
                    }
                    value={
                      activeTab === 'delivery' ? (
                        DeliveryAddress()
                      ) : (
                        <>
                          <Vault />
                          {startCase(
                            parseEnumType(
                              productLocationTypes,
                              wizardData?.selectedVault?.LocationType,
                            ),
                          )}
                          {vaultTag()}
                        </>
                      )
                    }
                    onClick={() => {
                      if (activeTab === 'vault') {
                        fetchVaultLocationPrices();
                      } else {
                        setOpenSelectAddress(true);
                      }
                    }}
                    onClickValue={() => {
                      if (
                        !addresses ||
                        (brokerCode === 'HAA' && isEmpty(verifiedAddresses))
                      ) {
                        setIsAddAddressOpen(true);
                      }
                    }}
                    isNotButton={
                      (activeTab === 'delivery' &&
                        (!addresses ||
                          (brokerCode === 'HAA' &&
                            isEmpty(verifiedAddresses)))) ||
                      isProductPage
                    }
                  />
                )}

                <RowItemElement
                  label={t('buyWizard.payWith')}
                  value={
                    <>
                      {
                        PAYMENT_METHOD_TYPES_ICONS[
                          selectedPaymentMethod?.PaymentMethodType
                        ]
                      }
                      {paymentMethodRowItem()}
                      {/* {selectedPaymentMethod &&
                        selectedPaymentMethod?.PaymentMethodType !== 3 && (
                          <Tag>{t('buyWizard.payment.savePrecent')}</Tag>
                        )} */}
                    </>
                  }
                  onClick={() => {
                    if (isPostPaidVisible) {
                      setOpenSelectPaymentOptions(true);
                    } else {
                      setIsFundsModalOpen(true);
                      // setWizardContent(IS_FUND_MODAL_OPEN, true);
                    }
                  }}
                  clickLabel={
                    !isPostPaidVisible
                      ? t('buyWizard.addFunds')
                      : t('buyWizard.changeLabel')
                  }
                />

                <ButtonWrapper>
                  <ModalButtons
                    isVertical
                    showOnlyPrimary
                    primaryButtonProps={{
                      type: 'submit',
                      label: t('buyWizard.reviewBuy'),
                      textTransform: 'capitalize',
                      marginTop: '10',
                      disabled:
                        (activeType === 'quantity' && !values.QuantityAmount) ||
                        (activeType === 'dollars' && !values.CashAmount) ||
                        !selectedPaymentMethod,
                      // disabled:
                      //   (activeType === 'Quantity' && selectedVault.IsCashOrderOnly) ||
                      //   errors?.CashAmount ||
                      //   errors?.QuantityAmount,
                      'data-cy': 'button-next',
                    }}
                  />
                </ButtonWrapper>
              </Form>
            )}
          </Formik>
        </BasicModalBody>
      </SectionLoader>

      <SelectVaultLocation
        open={openSelectVaultLocation}
        setOpen={setOpenSelectVaultLocation}
        wizardData={wizardData}
        setWizardData={setWizardData}
        lowestVault={firstLowestVault}
        setOpenSelectStorageType={setOpenSelectStorageType}
        accountUid={accountUid}
        sortedLocations={sortedLocations}
        isLoading={isVaultPricesLoading}
        locations={locations}
      />
      <SelectStorageType
        open={openSelectStorageType}
        setOpen={setOpenSelectStorageType}
        wizardData={wizardData}
        setWizardData={setWizardData}
        setOpenSelectVaultLocation={setOpenSelectVaultLocation}
        selectedPlan={selectedPlan}
      />
      <SelectAddress
        open={openSelectAddress}
        setOpen={setOpenSelectAddress}
        addresses={addresses}
        wizardData={wizardData}
        // setWizardData={setWizardData}
        setIsAddAddressOpen={setIsAddAddressOpen}
        isDeliveryAddressSelected={isDeliveryAddressSelected}
        setSelectedAddress={setSelectedAddress}
        selectedAddress={selectedAddress}
        countries={countries}
        openSupport={openSupport}
        isSupportOpen={isSupportOpen}
        // setSelectedAddress={handleConfirmAddress}
      />
      <SelectPaymentOptions
        open={openSelectPaymentOptions}
        setOpen={setOpenSelectPaymentOptions}
        wizardData={wizardData}
        setWizardData={setWizardData}
        paymentMethods={updatedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        setIsAddAddressOpen={setIsAddAddressOpen}
        isAddAddressOpen={isAddAddressOpen}
        accountUid={accountUid}
      />
      {displayStandardOptionsPermission && isFundsModalOpen && (
        <StandardDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            // setWizardContent(IS_FUND_MODAL_OPEN, false);
            deleteWizardKey(STANDARD_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
          }}
          overModal
          avoidClearingStorage
        />
      )}

      {displayIRAEquityOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => setIsFundsModalOpen(false)}
          type={IRA_EQUITY_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAEntrustOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => setIsFundsModalOpen(false)}
          type={IRA_ENTRUST_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAMilleniumOptionsPermission && isFundsModalOpen && (
        <MilleniumDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => setIsFundsModalOpen(false)}
          overModal
          avoidClearingStorage
        />
      )}
    </>
  );
};

OrderSelection.propTypes = {
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
    }),
    activeTab: PropTypes.string,
    selectedPlan: PropTypes.string,
    activeType: PropTypes.string,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
      PricePerUnit: PropTypes.number,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.number,
      QuantityAmount: PropTypes.number,
    }),
    deliveryAddress: PropTypes.shape({}),
    paymentMethodType: PropTypes.string,
    isSegregated: PropTypes.bool,
  }),
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodType: PropTypes.number,
    PaymentMethodUid: PropTypes.number,
    Last4Digits: PropTypes.number,
    BankName: PropTypes.string,
    BankAccountMask: PropTypes.number,
  }),
  isProductPage: PropTypes.bool,
  setWizardData: PropTypes.func,
  isPostPaidVisible: PropTypes.bool,
  handleNext: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setSelectedPaymentMethod: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  stepIndex: PropTypes.number,
  currentStepIndex: PropTypes.number,
  isGS: PropTypes.bool,
  openSupport: PropTypes.func,
  isSupportOpen: PropTypes.bool,
  isUtmaAccount: PropTypes.bool,
  isIRAAccount: PropTypes.bool,
};

export default OrderSelection;
